<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import MobileDatePicker from '@/components/picker/MobileDatePicker.vue';
import MobileTimePicker from '@/components/picker/MobileTimePicker.vue';

// 모바일 감지 (뷰포트 기준). forceMobile prop이 true면 항상 모바일로 취급 (isMobile computed)
const viewportIsMobile = ref(false);
const checkMobile = () => {
  viewportIsMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

// 커스텀 피커 상태
const showDatePicker = ref(false);
const showTimePicker = ref(false);

const handleDateConfirm = ({ year, month, day }) => {
  emit('update:formData', {
    ...props.formData,
    year,
    month,
    day,
  });
};

const handleTimeConfirm = ({ hour, minute }) => {
  emit('update:formData', {
    ...props.formData,
    hour,
    min: minute,
    birth_time: false,
  });
};

// Props 정의 (기존과 동일)
const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
  pgmode: String,
  areaTxt: String,
  isOptionInput: Boolean,
  isCheckboxChecked: Boolean,
  // 뷰포트와 무관하게 항상 모바일 폼으로 렌더 (시트/오버레이 등에서 사용)
  forceMobile: { type: Boolean, default: false },
});

// 뷰포트 모바일 OR forceMobile → 모바일 폼
const isMobile = computed(() => props.forceMobile || viewportIsMobile.value);

// Events 정의 (기존과 동일, 'openPopGanjiBox' -> 'openGanjiPopup'로 통일)
const emit = defineEmits([
  'update:formData',
  'update:areaTxt',
  'update:isOptionInput',
  'search',
  'resetReport',
  'openGanjiPopup', // 'openPopGanjiBox' 대신 'openGanjiPopup'으로 통일
  'openAreaPopup',
  'openTooltip',
  'openSummerTime',
]);

// --- 1. DOM 조작 대신 Template Ref 사용 ---

// 1-1. 템플릿의 VTextField를 참조할 ref 변수 선언
const yearField = ref(null);
const monthField = ref(null);
const dayField = ref(null);
const hourField = ref(null);
const minField = ref(null);

// 1-2. 문자열 이름과 ref 변수를 매핑하는 객체
const fieldRefs = {
  yearField,
  monthField,
  dayField,
  hourField,
  minField,
};

// 분 필드 엔터 핸들러: PC에서는 조회, 모바일에서는 키보드 닫기
const handleMinEnter = () => {
  if (isMobile.value) {
    minField.value?.$el?.querySelector('input')?.blur();
  } else {
    emit('search');
  }
};

// 1-3. [수정됨] getElementById 대신 ref로 포커스 이동
const focusNext = (fieldName) => {
  const targetRef = fieldRefs[fieldName];
  if (targetRef && targetRef.value) {
    targetRef.value.focus(); // Vuetify 컴포넌트의 focus 메서드 호출
  }
};

// 1-4. [수정됨] getElementById 대신 ref로 포커스 이동
const checkLength = (nextFieldName, value) => {
  if (value) {
    // 년도는 4자리, 나머지는 2자리가 찼을 때 다음 필드로 이동
    if (nextFieldName === 'monthField' && value.length === 4) {
      focusNext(nextFieldName);
    } else if (nextFieldName !== 'monthField' && value.length === 2) {
      focusNext(nextFieldName);
    }
  }
};

// --- 2. Props 직접 수정을 방지하는 헬퍼 함수 ---

// 2-1. [추가됨] v-model을 위한 formData 업데이트 헬퍼
// (예: 'year', '2025')를 받아서 formData를 복사하고 emit
const updateFormData = (key, value) => {
  emit('update:formData', {
    ...props.formData,
    [key]: value,
  });
};

// 2-2. [수정됨] '시간 모름' 체크박스 핸들러
const handleBirthTime = (event) => {
  const newBirthTime = event.target.checked; // v-model이 이미 토글했으므로 event에서 가져옴
  const newFormData = { ...props.formData, birth_time: newBirthTime };

  if (newBirthTime) {
    // '시간 모름'이 true가 되면
    newFormData.hour = '--';
    newFormData.min = '--';
  } else {
    // '시간 모름'이 false가 되면
    newFormData.hour = '';
    newFormData.min = '';
  }
  emit('update:formData', newFormData); // 수정된 객체 전체를 emit
};

// [수정됨] '원래 코드' 의도를 반영한 윤달 핸들러
const handleLeapYearChange = (event) => {
  const newLeapValue = event.target.checked; // (true 또는 false)

  // '원래 코드'의 로직을 여기에 구현
  let newLunarValue;
  if (newLeapValue === true) {
    // 윤달을 체크했을 때 '원래 코드'는 isLunar를 null로 설정
    newLunarValue = null;
    // (참고: 비즈니스 로직상 'true'(음력)가 맞다면 이 부분을 수정해야 합니다)
  } else {
    // 윤달을 해제했을 때 '원래 코드'는 isLunar를 false(양력)로 설정
    newLunarValue = 'false';
  }

  // [핵심] isLeapYear와 isLunar를 한 번에 emit
  emit('update:formData', {
    ...props.formData,
    isLeapYear: newLeapValue,
    isLunar: newLunarValue,
  });
};

// [수정됨] '원래 코드' 의도를 반영한 양력/음력 핸들러
const handleLeapLunarChange = (event) => {
  const newLunarValue = event.target.value; // ('false' 또는 'true')

  // [핵심] isLunar를 바꾸고, isLeapYear는 null로 해제
  emit('update:formData', {
    ...props.formData,
    isLunar: newLunarValue,
    isLeapYear: null,
  });
};

</script>

<template>
  <div class="search-box w-short">
    <div class="item-box">
      <div class="txt wd100 birth-gender-wrap">
        <div class="txt-gender-box">
          <input
            id="rd-genter-male"
            name="gender"
            type="radio"
            class="rd-gender male"
            value="1"
            :checked="props.formData.gender === '1'"
            @change="updateFormData('gender', $event.target.value)"
          /><label class="" for="rd-genter-male">남자</label>
          <input
            id="rd-genter-female"
            name="gender"
            type="radio"
            class="rd-gender female"
            value="0"
            :checked="props.formData.gender === '0'"
            @change="updateFormData('gender', $event.target.value)"
          /><label class="line" for="rd-genter-female">여자</label>
        </div>
        <div class="txt-gender-box">
          <input
            id="date1"
            type="radio"
            name="isLunar"
            class="rd-orange"
            value="false"
            :checked="props.formData.isLunar === 'false'"
            @change="handleLeapLunarChange"
          /><label for="date1">양력</label>
          <input
            id="date2"
            type="radio"
            name="isLunar"
            class="rd-orange"
            value="true"
            :checked="props.formData.isLunar === 'true'"
            @change="handleLeapLunarChange"
          /><label class="line" for="date2">음력</label>
          <input
            id="date3"
            type="checkbox"
            name="isLeapYear"
            class="rd-orange"
            :checked="props.formData.isLeapYear"
            @change="handleLeapYearChange"
          /><label class="line" for="date3">윤달</label>
        </div>
        <span class="btn-right">
          <button class="btn-bg-point" type="button" @click="emit('openGanjiPopup')">
            사주간지 입력
          </button>
        </span>
      </div>
    </div>
    <div class="item-box">
      <div class="txt-year-box">
        <span class="input-relative">
          <VTextField
            ref="yearField"
            :modelValue="props.formData.year"
            @update:modelValue="
              updateFormData('year', $event);
              checkLength('monthField', $event);
            "
            class="h-small inputNext align-center"
            variant="underlined"
            maxlength="4"
            inputmode="numeric"
            enterkeyhint="done"
            @keydown.enter="focusNext('monthField')"
          >
            <template #append-inner>
              <span class="txt-r">년</span>
            </template>
          </VTextField>
        </span>
        <span class="input-relative">
          <VTextField
            ref="monthField"
            id="monthField"
            :modelValue="props.formData.month"
            @update:modelValue="
              updateFormData('month', $event);
              checkLength('dayField', $event);
            "
            class="h-small inputNext align-center"
            variant="underlined"
            maxlength="2"
            inputmode="numeric"
            enterkeyhint="done"
            @keydown.enter="focusNext('dayField')"
          >
            <template #append-inner>
              <span class="txt-r">월</span>
            </template>
          </VTextField>
        </span>
        <span class="input-relative">
          <VTextField
            ref="dayField"
            id="dayField"
            :modelValue="props.formData.day"
            @update:modelValue="
              updateFormData('day', $event);
              checkLength('hourField', $event);
            "
            class="h-small inputNext align-center"
            variant="underlined"
            maxlength="2"
            inputmode="numeric"
            enterkeyhint="done"
            @keydown.enter="focusNext('hourField')"
          >
            <template #append-inner>
              <span class="txt-r">일</span>
            </template>
          </VTextField>
        </span>
        <!-- 모바일 달력 버튼 -->
        <button v-if="isMobile" type="button" class="mobile-picker-btn" @click="showDatePicker = true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="#888" stroke-width="1.5"/>
            <line x1="3" y1="9" x2="21" y2="9" stroke="#888" stroke-width="1.5"/>
            <line x1="8" y1="2" x2="8" y2="5" stroke="#888" stroke-width="1.5" stroke-linecap="round"/>
            <line x1="16" y1="2" x2="16" y2="5" stroke="#888" stroke-width="1.5" stroke-linecap="round"/>
            <circle cx="8" cy="14" r="1" fill="#888"/>
            <circle cx="12" cy="14" r="1" fill="#888"/>
            <circle cx="16" cy="14" r="1" fill="#888"/>
            <circle cx="8" cy="18" r="1" fill="#888"/>
            <circle cx="12" cy="18" r="1" fill="#888"/>
          </svg>
        </button>
        <!-- 모바일에서 줄바꿈용 -->
        <div v-if="isMobile" class="flex-break"></div>
        <span class="input-relative">
          <VTextField
            ref="hourField"
            id="hourField"
            :modelValue="props.formData.hour"
            @update:modelValue="
              updateFormData('hour', $event);
              checkLength('minField', $event);
            "
            class="h-small inputNext align-center"
            variant="underlined"
            maxlength="2"
            inputmode="numeric"
            enterkeyhint="done"
            :disabled="props.isCheckboxChecked"
            @keydown.enter="focusNext('minField')"
          >
            <template #append-inner>
              <span class="txt-r">시</span>
            </template>
          </VTextField>
        </span>
        <span class="input-relative">
          <VTextField
            ref="minField"
            id="minField"
            :modelValue="props.formData.min"
            @update:modelValue="updateFormData('min', $event)"
            class="h-small inputNext align-center"
            variant="underlined"
            maxlength="2"
            inputmode="numeric"
            :enterkeyhint="isMobile ? 'done' : 'search'"
            :disabled="props.isCheckboxChecked"
            @keydown.enter.prevent="handleMinEnter"
          >
            <template #append-inner>
              <span class="txt-r">분</span>
            </template>
          </VTextField>
        </span>
        <!-- 모바일 시계 버튼 -->
        <button v-if="isMobile && !props.isCheckboxChecked" type="button" class="mobile-picker-btn" @click="showTimePicker = true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="#888" stroke-width="1.5"/>
            <line x1="12" y1="7" x2="12" y2="12" stroke="#888" stroke-width="1.5" stroke-linecap="round"/>
            <line x1="12" y1="12" x2="16" y2="14" stroke="#888" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <span class="saju-option-checkbox">
          <input
            id="birth_time"
            type="checkbox"
            class="checkbox"
            v-model="props.formData.birth_time"
            @change="handleBirthTime"
          /><label class="label font" for="birth_time">시간 모름</label>
        </span>
      </div>
    </div>

    <div class="item-box">
      <div class="txt-text-box">
        <VTextField
          :modelValue="props.formData.userName"
          @update:modelValue="updateFormData('userName', $event)"
          class="h-small inputNext"
          style="flex: 1"
          placeholder="이름, 닉네임 (최대20자 입력, 선택사항)"
          variant="underlined"
          clearable
          clear-icon="ni-12-close-circle"
          maxlength="20"
        />
        <VTextField
          ref="areaField"
          :modelValue="props.areaTxt"
          @update:modelValue="emit('update:areaTxt', $event)"
          class="h-small inputNext"
          style="flex: 1"
          placeholder="출생 지역 검색 (해외 출생지 포함)"
          variant="underlined"
          readonly
          clearable
          clear-icon="ni-12-close-circle"
          @click="emit('openAreaPopup')"
        />
      </div>
    </div>
  </div>

  <!-- 커스텀 피커 -->
  <MobileDatePicker
    v-model="showDatePicker"
    :year="props.formData.year"
    :month="props.formData.month"
    :day="props.formData.day"
    @confirm="handleDateConfirm"
  />
  <MobileTimePicker
    v-model="showTimePicker"
    :hour="props.formData.hour"
    :minute="props.formData.min"
    @confirm="handleTimeConfirm"
  />
</template>

<style scoped>
.mobile-picker-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: #f5f5f5;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.mobile-picker-btn:active {
  background: #e8e8e8;
}
</style>
